import React, { useCallback, useEffect, useState } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { Pencil, Plus, Rocket, Trash2 } from 'lucide-react';
import { useAdminAuth } from '../AdminAuthContext';
import {
  deleteCategory,
  fetchCategoriesAdmin,
  fetchHomepagePublishSettings,
  fetchMoviesAdmin,
  fetchMoviesForCategory,
  insertCategory,
  publishHomepageCategories,
  setCollectionMembership,
  updateCategory
} from '../adminApi';

function slugifyName(name) {
  const s = name
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
  return s || 'collection';
}

const emptyForm = {
  name: '',
  slug: '',
  color: '#6366f1'
};

export default function CategoriesPage() {
  const { canEditMovies } = useAdminAuth();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [slugTouched, setSlugTouched] = useState(false);
  const [publishDialogOpen, setPublishDialogOpen] = useState(false);
  const [publishSettings, setPublishSettings] = useState(null);
  const [publishing, setPublishing] = useState(false);
  /** @type {{ video_id: string, title: string }[]} */
  const [collectionMovies, setCollectionMovies] = useState([]);
  const [movieSearch, setMovieSearch] = useState('');
  const [movieSearchResults, setMovieSearchResults] = useState([]);
  const [movieSearchLoading, setMovieSearchLoading] = useState(false);

  const loadPublish = useCallback(async () => {
    const { settings } = await fetchHomepagePublishSettings();
    setPublishSettings(settings);
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    const res = await fetchCategoriesAdmin();
    if (res.error) {
      setError(res.error.message || 'Failed to load collections');
      setRows([]);
    } else {
      setRows(res.rows || []);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    loadPublish();
  }, [loadPublish]);

  const confirmPublishCategories = async () => {
    if (!canEditMovies) return;
    setPublishing(true);
    const { error } = await publishHomepageCategories();
    setPublishing(false);
    if (error) {
      alert(error.message || 'Publish failed');
      return;
    }
    await loadPublish();
    setPublishDialogOpen(false);
  };

  const openCreate = () => {
    setEditingId(null);
    setForm(emptyForm);
    setSlugTouched(false);
    setCollectionMovies([]);
    setMovieSearch('');
    setMovieSearchResults([]);
    setDialogOpen(true);
  };

  const openEdit = async (row) => {
    setEditingId(row.id);
    setForm({
      name: row.name || '',
      slug: row.slug || '',
      color: row.color || '#6366f1'
    });
    setSlugTouched(true);
    setMovieSearch('');
    setMovieSearchResults([]);
    const { rows: inCollection } = await fetchMoviesForCategory(row.id);
    setCollectionMovies(inCollection || []);
    setDialogOpen(true);
  };

  const saveCategory = async (e) => {
    e.preventDefault();
    if (!canEditMovies) return;
    const name = form.name.trim();
    const slug = form.slug.trim().toLowerCase();
    const color = form.color.trim() || '#6366f1';
    if (!name) {
      alert('Name is required.');
      return;
    }
    if (!slug || !/^[a-z0-9]+(-[a-z0-9]+)*$/.test(slug)) {
      alert(
        'Slug must be lowercase letters, numbers, and single hyphens between words (e.g. new-releases).'
      );
      return;
    }

    setSaving(true);
    let err;
    let categoryId = editingId;
    if (editingId) {
      const { error: e1 } = await updateCategory(editingId, {
        name,
        slug,
        color
      });
      err = e1;
    } else {
      const { data: created, error: e2 } = await insertCategory({
        name,
        slug,
        color
      });
      err = e2;
      categoryId = created?.id ?? null;
    }

    if (!err && categoryId) {
      const { error: syncErr } = await setCollectionMembership(
        categoryId,
        collectionMovies.map((m) => m.video_id)
      );
      if (syncErr) err = syncErr;
    }

    setSaving(false);
    if (err) {
      alert(err.message || 'Save failed');
      return;
    }
    setDialogOpen(false);
    load();
  };

  useEffect(() => {
    let cancelled = false;
    const q = movieSearch.trim();
    if (q.length < 2) {
      setMovieSearchResults([]);
      return undefined;
    }
    setMovieSearchLoading(true);
    const t = setTimeout(async () => {
      const res = await fetchMoviesAdmin({
        page: 1,
        pageSize: 15,
        search: q,
        channel: '',
        sortField: 'published_date',
        sortAsc: false
      });
      if (!cancelled) {
        setMovieSearchLoading(false);
        if (res.error) setMovieSearchResults([]);
        else setMovieSearchResults(res.rows || []);
      }
    }, 300);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [movieSearch]);

  const addMovieToCollection = (row) => {
    const id = row.video_id;
    if (collectionMovies.some((m) => m.video_id === id)) return;
    setCollectionMovies((prev) => [
      ...prev,
      { video_id: id, title: row.title || id }
    ]);
  };

  const removeMovieFromCollection = (videoId) => {
    setCollectionMovies((prev) => prev.filter((m) => m.video_id !== videoId));
  };

  const remove = async (row) => {
    if (!canEditMovies) return;
    if (
      !window.confirm(
        `Delete collection “${row.name}”? Movies will be unlinked from this collection.`
      )
    ) {
      return;
    }
    const { error: delErr } = await deleteCategory(row.id);
    if (delErr) {
      alert(delErr.message || 'Delete failed');
      return;
    }
    load();
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-white">Collections</h1>
          <p className="text-sm text-zinc-500 mt-1">
            Name, slug, color, and movies in this row. You can also assign from the
            Movies page — use{' '}
            <strong className="text-zinc-400 font-medium">Publish collections</strong>{' '}
            so the public homepage shows these rows.
          </p>
        </div>
        {canEditMovies && (
          <div className="flex flex-wrap items-center gap-2 shrink-0">
            <span
              className={`text-xs font-medium px-2.5 py-1 rounded-md border ${
                publishSettings?.categories_published
                  ? 'border-emerald-800 bg-emerald-950/50 text-emerald-300'
                  : 'border-zinc-700 bg-zinc-900 text-zinc-500'
              }`}
            >
              {publishSettings?.categories_published
                ? 'Live on homepage'
                : 'Not published'}
            </span>
            <button
              type="button"
              onClick={() => setPublishDialogOpen(true)}
              className="inline-flex items-center gap-2 rounded-lg border border-amber-800/60 bg-amber-950/40 px-3 py-2 text-sm font-medium text-amber-200 hover:bg-amber-950/70"
            >
              <Rocket className="h-4 w-4" />
              Publish collections
            </button>
            <button
              type="button"
              onClick={openCreate}
              className="inline-flex items-center gap-2 rounded-lg bg-violet-600 px-3 py-2 text-sm font-medium text-white hover:bg-violet-500"
            >
              <Plus className="h-4 w-4" />
              Add collection
            </button>
          </div>
        )}
      </div>

      {error && <div className="text-sm text-red-400">{error}</div>}

      <div className="rounded-xl border border-zinc-800 overflow-x-auto">
        <table className="w-full text-sm text-left">
          <thead className="bg-zinc-900/80 text-zinc-400 text-xs uppercase">
            <tr>
              <th className="px-3 py-2">Name</th>
              <th className="px-3 py-2">Slug</th>
              <th className="px-3 py-2">Color</th>
              <th className="px-3 py-2 tabular-nums">Movies</th>
              {canEditMovies && <th className="px-3 py-2 w-28" />}
            </tr>
          </thead>
          <tbody className="divide-y divide-zinc-800">
            {loading ? (
              <tr>
                <td
                  colSpan={canEditMovies ? 5 : 4}
                  className="px-3 py-8 text-center text-zinc-500"
                >
                  Loading…
                </td>
              </tr>
            ) : (
              rows.map((r) => (
                <tr key={r.id} className="hover:bg-zinc-900/50">
                  <td className="px-3 py-2 text-white">{r.name}</td>
                  <td className="px-3 py-2 font-mono text-xs text-zinc-400">
                    {r.slug}
                  </td>
                  <td className="px-3 py-2">
                    <span className="inline-flex items-center gap-2">
                      <span
                        className="inline-block h-5 w-5 rounded border border-zinc-600"
                        style={{ backgroundColor: r.color }}
                        title={r.color}
                      />
                      <span className="text-zinc-500 text-xs">{r.color}</span>
                    </span>
                  </td>
                  <td className="px-3 py-2 tabular-nums text-zinc-300">
                    {(r.movie_count ?? 0).toLocaleString()}
                  </td>
                  {canEditMovies && (
                    <td className="px-3 py-2">
                      <div className="flex gap-1">
                        <button
                          type="button"
                          onClick={() => openEdit(r)}
                          className="p-1.5 rounded text-zinc-400 hover:text-white hover:bg-zinc-800"
                          title="Edit"
                        >
                          <Pencil className="h-4 w-4" />
                        </button>
                        <button
                          type="button"
                          onClick={() => remove(r)}
                          className="p-1.5 rounded text-zinc-400 hover:text-red-300 hover:bg-zinc-800"
                          title="Delete"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  )}
                </tr>
              ))
            )}
            {!loading && rows.length === 0 && (
              <tr>
                <td
                  colSpan={canEditMovies ? 5 : 4}
                  className="px-3 py-8 text-center text-zinc-500"
                >
                  No collections yet.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <Dialog.Root open={publishDialogOpen} onOpenChange={setPublishDialogOpen}>
        <Dialog.Portal>
          <Dialog.Overlay className="fixed inset-0 bg-black/70 z-50" />
          <Dialog.Content className="fixed left-1/2 top-1/2 z-50 w-[min(100vw-2rem,440px)] -translate-x-1/2 -translate-y-1/2 rounded-xl border border-zinc-700 bg-zinc-900 p-6 shadow-xl focus:outline-none">
            <Dialog.Title className="text-lg font-semibold text-white mb-2">
              Publish collections to homepage?
            </Dialog.Title>
            <p className="text-sm text-zinc-400 mb-6">
              Visitors will see your current collections and the movies assigned to
              them on the public homepage (replacing automatic collection rows).
              Publish again after major changes if you want to confirm the go-live
              step.
            </p>
            <div className="flex justify-end gap-2">
              <Dialog.Close asChild>
                <button
                  type="button"
                  className="rounded-lg px-3 py-2 text-sm text-zinc-400 hover:bg-zinc-800"
                  disabled={publishing}
                >
                  Cancel
                </button>
              </Dialog.Close>
              <button
                type="button"
                disabled={publishing || !canEditMovies}
                onClick={confirmPublishCategories}
                className="rounded-lg bg-amber-600 px-4 py-2 text-sm font-medium text-white hover:bg-amber-500 disabled:opacity-50"
              >
                {publishing ? 'Publishing…' : 'Yes, publish'}
              </button>
            </div>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>

      <Dialog.Root open={dialogOpen} onOpenChange={setDialogOpen}>
        <Dialog.Portal>
          <Dialog.Overlay className="fixed inset-0 bg-black/70 z-50" />
          <Dialog.Content className="fixed left-1/2 top-1/2 z-50 w-[min(100vw-2rem,560px)] max-h-[90vh] overflow-y-auto -translate-x-1/2 -translate-y-1/2 rounded-xl border border-zinc-700 bg-zinc-900 p-6 shadow-xl focus:outline-none">
            <Dialog.Title className="text-lg font-semibold text-white mb-4">
              {editingId ? 'Edit collection' : 'Add collection'}
            </Dialog.Title>
            <form onSubmit={saveCategory} className="space-y-3">
              <div>
                <label className="text-xs text-zinc-500">Name</label>
                <input
                  className="mt-1 w-full rounded border border-zinc-700 bg-zinc-950 px-2 py-1.5 text-sm text-white"
                  value={form.name}
                  onChange={(e) => {
                    const v = e.target.value;
                    setForm((f) => ({
                      ...f,
                      name: v,
                      ...(!editingId && !slugTouched
                        ? { slug: slugifyName(v) }
                        : {})
                    }));
                  }}
                  required
                />
              </div>
              <div>
                <label className="text-xs text-zinc-500">Slug</label>
                <input
                  className="mt-1 w-full rounded border border-zinc-700 bg-zinc-950 px-2 py-1.5 text-sm text-white font-mono"
                  value={form.slug}
                  onChange={(e) => {
                    setSlugTouched(true);
                    setForm((f) => ({
                      ...f,
                      slug: e.target.value.toLowerCase()
                    }));
                  }}
                  required
                />
              </div>
              <div>
                <label className="text-xs text-zinc-500">Color</label>
                <div className="mt-1 flex gap-2 items-center">
                  <input
                    type="color"
                    className="h-9 w-12 cursor-pointer rounded border border-zinc-700 bg-zinc-950 p-0"
                    value={
                      /^#[0-9A-Fa-f]{6}$/.test(form.color)
                        ? form.color
                        : '#6366f1'
                    }
                    onChange={(e) =>
                      setForm((f) => ({ ...f, color: e.target.value }))
                    }
                  />
                  <input
                    className="flex-1 rounded border border-zinc-700 bg-zinc-950 px-2 py-1.5 text-sm text-white font-mono"
                    value={form.color}
                    onChange={(e) =>
                      setForm((f) => ({ ...f, color: e.target.value.trim() }))
                    }
                    placeholder="#6366f1"
                  />
                </div>
              </div>

              <div className="border-t border-zinc-800 pt-3 mt-1">
                <label className="text-xs text-zinc-500">Movies in this collection</label>
                <p className="text-xs text-zinc-600 mt-0.5 mb-2">
                  Search and add titles; remove with the trash icon. Saving updates
                  links for this collection only.
                </p>
                {collectionMovies.length > 0 && (
                  <ul className="mb-3 max-h-36 overflow-y-auto rounded-lg border border-zinc-800 divide-y divide-zinc-800">
                    {collectionMovies.map((m) => (
                      <li
                        key={m.video_id}
                        className="flex items-center justify-between gap-2 px-2 py-1.5 text-sm"
                      >
                        <div className="min-w-0">
                          <div className="text-white truncate">{m.title}</div>
                          <div className="text-[11px] text-zinc-500 font-mono truncate">
                            {m.video_id}
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={() => removeMovieFromCollection(m.video_id)}
                          disabled={!canEditMovies}
                          className="shrink-0 p-1.5 rounded text-zinc-500 hover:text-red-300 hover:bg-zinc-800"
                          title="Remove from collection"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
                {canEditMovies && (
                  <div className="space-y-2">
                    <input
                      type="search"
                      placeholder="Search movies by title, channel… (min. 2 characters)"
                      value={movieSearch}
                      onChange={(e) => setMovieSearch(e.target.value)}
                      className="w-full rounded-lg border border-zinc-700 bg-zinc-950 px-3 py-2 text-sm text-white"
                    />
                    {movieSearchLoading && (
                      <div className="text-xs text-zinc-500">Searching…</div>
                    )}
                    {movieSearch.trim().length >= 2 &&
                      !movieSearchLoading &&
                      movieSearchResults.length > 0 && (
                        <ul className="max-h-40 overflow-y-auto rounded-lg border border-zinc-800 divide-y divide-zinc-800">
                          {movieSearchResults.map((row) => {
                            const inList = collectionMovies.some(
                              (m) => m.video_id === row.video_id
                            );
                            return (
                              <li
                                key={row.video_id}
                                className="flex items-center justify-between gap-2 px-2 py-1.5 text-sm"
                              >
                                <div className="min-w-0">
                                  <div className="text-white truncate">{row.title}</div>
                                  <div className="text-[11px] text-zinc-500 font-mono truncate">
                                    {row.video_id}
                                  </div>
                                </div>
                                <button
                                  type="button"
                                  disabled={inList}
                                  onClick={() => addMovieToCollection(row)}
                                  className="shrink-0 text-xs rounded-md bg-zinc-800 px-2 py-1 text-zinc-200 hover:bg-zinc-700 disabled:opacity-40"
                                >
                                  {inList ? 'Added' : 'Add'}
                                </button>
                              </li>
                            );
                          })}
                        </ul>
                      )}
                  </div>
                )}
              </div>

              <div className="flex justify-end gap-2 pt-2">
                <Dialog.Close asChild>
                  <button
                    type="button"
                    className="rounded-lg px-3 py-2 text-sm text-zinc-400 hover:bg-zinc-800"
                  >
                    Cancel
                  </button>
                </Dialog.Close>
                <button
                  type="submit"
                  disabled={saving || !canEditMovies}
                  className="rounded-lg bg-violet-600 px-4 py-2 text-sm text-white hover:bg-violet-500 disabled:opacity-50"
                >
                  {saving ? 'Saving…' : 'Save'}
                </button>
              </div>
            </form>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>
    </div>
  );
}
